import { useCallback, useMemo, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { FS_XS } from '../common/theme'
import type { TextStructure } from '../types/textstructure'

interface HoveredSentence {
  key: string
  color?: string
}

interface SentencePosition {
  // Container-relative y of the sentence's first visible line.
  top: number
  // Rendered line-height in px of that first line (used to centre
  // the fixed-size gutter block on it).
  lineHeight: number
}

interface TaResponseGutterColumnProps {
  structure?: TextStructure | null
  // When set, clicking a gutter block scrolls the matching source
  // sentence into view (same behaviour as ProView's sim-icon click).
  linkId?: string
  // Fires with the currently-hovered sentence's `key`
  // (`${pi}-${li}-${sj}`) + its sim `color`, or `null` on mouse-out.
  // Lets a sibling text renderer paint the matching sentence in
  // lock-step (same colour) with the gutter block.
  onSentenceHover?: (hover: HoveredSentence | null) => void
  // Optional map from sentence key → measured first-line position in
  // the text pane. Parent runs a `ResizeObserver` over the text
  // container and reports `getClientRects()[0]` per span. Empty until
  // the first measurement lands, so the gutter renders nothing until
  // it knows where each block belongs.
  sentencePositions?: Map<string, SentencePosition>
}

// Fixed-size block per sentence — 12 x 12 px, coloured by the
// sentence's sim score. Each block is anchored to the top of its
// sentence's first visible line in the text pane; when several
// sentences start on the same line their blocks sit side-by-side.
const BLOCK_SIZE = 12
const BLOCK_GAP = 3

// Vertical tolerance (px) for treating two sentences as "starting on
// the same line". Anything closer than this collapses into one row of
// side-by-side blocks. Slightly larger than typical sub-pixel jitter
// but well under a single text line.
const LINE_MERGE_TOLERANCE = 3

export default function TaResponseGutterColumn({
  structure,
  linkId,
  onSentenceHover,
  sentencePositions,
}: TaResponseGutterColumnProps) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const enterHover = (key: string, color?: string) => {
    setHoveredKey(key)
    onSentenceHover?.({ key, color })
  }
  const leaveHover = () => {
    setHoveredKey(null)
    onSentenceHover?.(null)
  }

  const scrollToSource = useCallback((sourceSequenceSha?: string) => {
    if (!linkId || !sourceSequenceSha) return
    const el = document.getElementById(`${linkId}_${sourceSequenceSha}_drop`)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }, [linkId])

  // Flatten sentences in reading order and group by the top of their
  // first visible line. Each group ends up as one row of blocks in
  // the gutter, anchored to that line's y-coord.
  const rows = useMemo(() => {
    type Item = { key: string; sent: any }
    type Row = { top: number; lineHeight: number; items: Item[] }
    const out: Row[] = []
    if (!structure?.paragraphs || !sentencePositions) return out
    for (const [pi, para] of structure.paragraphs.entries()) {
      for (const [li, line] of (para.lines ?? []).entries()) {
        for (const [sj, sent] of (line.sentences ?? []).entries()) {
          const key = `${pi}-${li}-${sj}`
          const pos = sentencePositions.get(key)
          if (!pos) continue
          const last = out[out.length - 1]
          if (last && Math.abs(last.top - pos.top) < LINE_MERGE_TOLERANCE) {
            last.items.push({ key, sent })
            // Keep the tallest line-height in case sentences on the
            // same visual line have different metrics (unlikely, but
            // cheap to be safe).
            if (pos.lineHeight > last.lineHeight) last.lineHeight = pos.lineHeight
          } else {
            out.push({ top: pos.top, lineHeight: pos.lineHeight, items: [{ key, sent }] })
          }
        }
      }
    }
    return out
  }, [structure, sentencePositions])

  // Width big enough to fit the widest row of blocks. Falls back to
  // one block's width so the column doesn't collapse to zero on
  // first render (before measurements arrive). Oscillation between
  // widths (gutter width change → text reflow → new measurement) is
  // held in check by the parent's width-keyed measurement cache —
  // repeated ticks at widths we've already measured short-circuit.
  const maxBlocksInRow = rows.reduce((n, r) => Math.max(n, r.items.length), 1)
  const gutterWidth = maxBlocksInRow * BLOCK_SIZE + (maxBlocksInRow - 1) * BLOCK_GAP

  if (!structure?.paragraphs?.length) return null

  return (
    <Box
      data-testid="ta_gutter_column"
      sx={{
        width: gutterWidth,
        flexShrink: 0,
        // Absolute-positioned rows anchor to this element's top edge,
        // which sits flush with the top of the text container thanks
        // to the surrounding flex row + `alignItems: 'stretch'`. No
        // padding is needed — the `top` values from the measurement
        // are already container-relative, so they include TaResponse's
        // border + inner `py` offset.
        position: 'relative',
        alignSelf: 'stretch',
      }}
    >
      {rows.map((row, ri) => {
        // Centre the 12 px block vertically on the sentence's first
        // text line so a normal 1-line sentence's block sits over the
        // middle of that line.
        const yTop = row.top + Math.max(0, Math.round((row.lineHeight - BLOCK_SIZE) / 2))
        return (
          <Box
            key={ri}
            sx={{
              position: 'absolute',
              top: `${yTop}px`,
              left: 0,
              display: 'flex',
              gap: `${BLOCK_GAP}px`,
            }}
          >
            {row.items.map(({ key, sent }) => {
              const isHovered = hoveredKey === key
              const simInt = sent.sim?.simint
              const color = sent.sim?.defaultcolor ?? 'rgba(0,0,0,0.12)'
              const canClick = !!linkId && !!sent.sim?.sourcesequencesha
              return (
                <Tooltip
                  key={key}
                  title={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxWidth: 320 }}>
                      <Typography variant="caption" sx={{ fontSize: FS_XS, fontWeight: 700 }}>
                        {simInt != null ? `${simInt}% match` : 'No match'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: FS_XS,
                          // Real source sentences read normally; the
                          // "cannot attribute" fallback is italic and
                          // dimmer so it doesn't look like content.
                          opacity: sent.sim?.sourcesentence ? 0.85 : 0.55,
                          fontStyle: sent.sim?.sourcesentence ? 'normal' : 'italic',
                        }}
                      >
                        {sent.sim?.sourcesentence ?? 'Cannot attribute sentence'}
                      </Typography>
                      {sent.sim?.filename && (
                        <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.65, fontWeight: 600 }}>
                          ({sent.sim.filename})
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                >
                  <Box
                    onMouseEnter={() => enterHover(key, sent.sim?.defaultcolor)}
                    onMouseLeave={leaveHover}
                    onClick={() => scrollToSource(sent.sim?.sourcesequencesha)}
                    sx={{
                      width: BLOCK_SIZE,
                      height: BLOCK_SIZE,
                      flexShrink: 0,
                      borderRadius: '2px',
                      bgcolor: color,
                      cursor: canClick ? 'pointer' : 'default',
                      outline: isHovered ? '2px solid' : 'none',
                      outlineColor: 'primary.main',
                      outlineOffset: '-1px',
                      transition: 'outline-color 0.12s ease',
                    }}
                  />
                </Tooltip>
              )
            })}
          </Box>
        )
      })}
    </Box>
  )
}
